import { useOperationQueue } from '../hooks/useOperationQueue';
import { useSelector } from 'react-redux';
import { RootState } from '../types/store';
import { RefreshIcon } from './RefreshIcon';
import { useState } from 'react';

interface SaveAndRefreshProps {
  onRefresh?: () => Promise<void>;
  className?: string;
  hidden?: boolean;
  clickToRefresh?: boolean; // Default false. If true, button becomes clickable. "Saved" gets replaced with "Refresh".
}

export function SaveAndRefresh({ onRefresh, className = '', hidden = false, clickToRefresh = false }: SaveAndRefreshProps) {
  useOperationQueue(); // Add the queue processor
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Get pending operations count for status display
  const pendingOperations = useSelector(
    (state: RootState) =>
      state.map3Pending.operations.filter((op) => op.status === 'pending').length,
  );

  const handleRefresh = async () => {
    if (!onRefresh) return;
    setIsRefreshing(true);
    await onRefresh();
    setIsRefreshing(false);
  };

  // Update status text based on mode and pending operations
  const pendingStatus = clickToRefresh
    ? (isRefreshing ? 'Refreshing...' : 'Refresh')
    : (isRefreshing
        ? 'Saving...'
        : pendingOperations > 0
          ? `Saving ${pendingOperations} changes...`
          : 'Saved');

  if (hidden) {
    // Just render a fragment to process the queue without UI
    return <></>;
  }

  return (
    <button
      onClick={handleRefresh}
      className={`flex items-center gap-2 px-4 py-2 text-gray-600 
        ${onRefresh ? 'hover:text-gray-800 hover:bg-gray-100 cursor-pointer rounded-md' : 'cursor-default'} 
        ${className}`}
      disabled={clickToRefresh ? (isRefreshing || !onRefresh) : (isRefreshing || !onRefresh || pendingOperations > 0)}
    >
      <RefreshIcon className="w-4 h-4" isRefreshing={isRefreshing} />
      <span className="min-w-[4rem] text-left">{pendingStatus}</span>
    </button>
  );
}
